import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";

const title = "Skolverket & kompletterande svenska | SIS Estepona";
const description =
  "Om Svenska Skolverket och kompletterande svensk undervisning för elever utomlands – språkutveckling, kulturell förståelse och anpassade program.";

export const Route = createFileRoute("/svenska-skolverket-och-kompletterande-svensk-undervisning")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/svenska-skolverket-och-kompletterande-svensk-undervisning") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/svenska-skolverket-och-kompletterande-svensk-undervisning"),
  }),
  component: Skolverket,
});

function Skolverket() {
  return (
    <>
      <PageHero
        eyebrow="Myndighet"
        title="Svenska Skolverket och kompletterande svensk undervisning"
        intro="Skolverket är den nationella myndighet som ansvarar för att utveckla, stödja och utvärdera utbildningssystemet i Sverige, med ett ansvarsområde som omfattar förskola, grundskola, gymnasieskola och vuxenutbildning."
      />

      <Prose>
        <h2>Skolverkets huvuduppgifter</h2>
        <ul>
          <li>
            <strong>Utveckling av läroplaner och kursplaner:</strong> Skolverket utformar och
            reviderar nationella läroplaner och kursplaner för att säkerställa att undervisningen
            håller hög standard och är relevant för dagens samhälle.
          </li>
          <li>
            <strong>Stöd till skolor och lärare:</strong> Myndigheten erbjuder resurser, fortbildning
            och vägledning för lärare och skolledare för att förbättra undervisningsmetoder och
            skolutveckling.
          </li>
          <li>
            <strong>Utvärdering och kvalitetssäkring:</strong> Genom att följa upp och utvärdera
            skolors resultat och arbetsmetoder identifieras och sprids goda exempel samtidigt som
            brister åtgärdas.
          </li>
          <li>
            <strong>Regler och riktlinjer:</strong> Skolverket ansvarar för att implementera och
            övervaka lagar och regler som styr den svenska utbildningen.
          </li>
        </ul>

        <h2>Kompletterande svensk undervisning</h2>
        <p>
          För elever som bor utomlands eller har behov av extra stöd i svenska språket erbjuder vi
          kompletterande svensk undervisning. Undervisningen syftar till att stärka elevernas
          kunskaper i svenska språket och svensk kultur, vilket är avgörande för att bibehålla och
          utveckla deras språkfärdigheter och kulturella identitet.
        </p>
        <p>
          Vi är godkända av Skolverket för kompletterande svenska. Den undervisningen följer
          Skolverkets kursplan i svenska för elever i utlandet. Godkännandet gäller just ämnet
          kompletterande svenska – skolan i övrigt drivs som en privat skola under tillsyn av Junta
          de Andalucía (den spanska utbildningsmyndigheten i Andalusien) och är inte en
          statsbidragsberättigad svensk utlandsskola.
        </p>

        <ul>
          <li>
            <strong>Språkutveckling:</strong> Kurserna fokuserar på läsning, skrivning, tal och
            lyssnande med moderna och interaktiva undervisningsmetoder.
          </li>
          <li>
            <strong>Kulturell förståelse:</strong> Undervisningen innehåller moment som belyser
            svensk kultur, historia och samhälle.
          </li>
          <li>
            <strong>Anpassade program:</strong> Flexibla program som kan anpassas efter elevernas
            individuella behov, både fysiska och digitala klasser samt individuella och gruppbaserade
            lärandemiljöer.
          </li>
          <li>
            <strong>Stöd och vägledning:</strong> Våra erfarna lärare ger kontinuerlig feedback och
            stöd som hjälper eleverna att övervinna utmaningar och nå sina personliga mål.
          </li>
        </ul>
        <p>
          <a
            href="https://www.skolverket.se/publikationsserier/styrdokument/2011/kursplan-i-svenska-for-kompletterande-undervisning-for-svenska-elever-i-utlandet?id=2718"
            target="_blank"
            rel="noopener noreferrer"
          >
            Läs mer om Skolverket här
          </a>
        </p>

        <h2>Hos oss är kompletterande svenska ett tillägg – inte hela skolgången</h2>
        <p>
          Kompletterande svensk undervisning är till för barn som har sin ordinarie skolgång i en
          spansk eller internationell skola. Det är värdefullt, men det är några timmar i veckan i
          ett ämne. Vår ordinarie verksamhet är något annat: en hel skoldag på svenska, med
          målet att all svensk undervisning ges av lärare med svensk lärarlegitimation, och undervisning i alla ämnen enligt svensk läroplan från
          förskoleklass till årskurs 6, och därefter fullständiga studier via Sofia Distans och
          Hermods med betyg enligt svenska regler.
        </p>
        <p>
          Med andra ord: vi är godkända av Skolverket för kompletterande svenska – och vi erbjuder
          dessutom en hel skoldag på svenska enligt svensk läroplan, som privat skola under spansk
          tillsyn. Familjer kan välja det ena, det andra eller kombinera, till exempel full skolgång
          för ett barn och kompletterande svenska för ett syskon som går kvar i en lokal skola.
        </p>



        <h2>Vad detta innebär för er som familj</h2>
        <p>
          För svenska familjer utomlands är kopplingen till det svenska skolsystemet ofta avgörande.
          Att undervisningen följer svensk läroplan gör det enklare att återvända till en skola i
          Sverige, och för äldre elever är det distansskolorna Sofia Distans och Hermods som utfärdar
          betyg enligt svenska regler.
        </p>
        <p>
          Vill ni veta vad som gäller i ert fall – exempelvis kring statsbidrag eller behörighet – är
          Skolverkets egen information alltid den som gäller. Vi hjälper gärna till att peka på rätt
          sida och att beskriva hur upplägget fungerar hos oss.
        </p>

        <h2>Läs vidare</h2>
        <ul>
          <li>
            <a className="text-primary link-quiet" href="/hogstadium-gymnasium">Högstadium och gymnasium</a>{" "}
            – hur distansstudierna fungerar i våra lokaler.
          </li>
          <li>
            <a className="text-primary link-quiet" href="/arskurser">Årskurser</a> – vilka stadier
            vi tar emot.
          </li>
          <li>
            <a className="text-primary link-quiet" href="/avgifter">Avgifter</a> – kostnader per
            termin och tillval.
          </li>
        </ul>


      </Prose>

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
